import { auth } from '../../config/firebase'
import { onAuthStateChanged } from 'firebase/auth'

class TokenManager {
  private static instance: TokenManager
  private currentToken: string | null = null
  private tokenExpiry: number | null = null
  private authReady: Promise<void>

  private constructor() {
    // Create a promise that resolves when auth state is determined
    this.authReady = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, () => {
        unsubscribe() // Unsubscribe after first auth state
        resolve()
      })
    })
  }

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager()
    }
    return TokenManager.instance
  }

  private isTokenExpired(): boolean {
    if (!this.tokenExpiry) return true
    // Add 5 minute buffer before actual expiry
    return Date.now() >= this.tokenExpiry - 5 * 60 * 1000
  }

  public async getToken(): Promise<string | null> {
    // Wait for auth to be ready
    await this.authReady

    // Get current user from Firebase
    const user = auth.currentUser
    if (!user) {
      this.currentToken = null
      return null
    }

    // Check if we have a valid cached token
    if (this.currentToken && !this.isTokenExpired()) {
      return this.currentToken
    }

    try {
      // Get fresh token from Firebase
      const token = await user.getIdToken()
      this.currentToken = token
      
      // Parse token to get expiry (tokens are JWT)
      const payload = JSON.parse(atob(token.split('.')[1]))
      this.tokenExpiry = payload.exp * 1000 // Convert to milliseconds
      
      return token
    } catch (error) {
      console.error('Failed to get token:', error)
      this.currentToken = null
      return null
    }
  }

  public clearToken(): void {
    this.currentToken = null
    this.tokenExpiry = null
  }

  public async waitForAuth(): Promise<void> {
    return this.authReady
  }
}

export const tokenManager = TokenManager.getInstance()